const normaliseLocale = (value = "") => value.replace(/[^a-z]/gi, "").toLowerCase();

// Which market a visitor belongs to: SE -> Swedish site, US/CA -> US shop, GB -> UK shop and
// everyone else -> the English (EU) catalogue on www.wallmek.se/en (en_SE, "English ROW",
// cf. NewsletterSignup.js). Canada is served by the US shop — the same rule the checkout
// encodes at Checkout.js:108.
const US_SERVED_COUNTRIES = ["US", "CA"];
const UK_SERVED_COUNTRIES = ["GB"];
export const ROW_LOCALE = "en_SE";

export const localeForCountry = (countryCode) => {
    if (countryCode === "SE") {
        return "sv_SE";
    }

    if (US_SERVED_COUNTRIES.includes(countryCode)) {
        return "en_US";
    }

    if (UK_SERVED_COUNTRIES.includes(countryCode)) {
        return "en_GB";
    }

    return ROW_LOCALE;
};

// hreflang describes the region a page is for, not the spelling it uses, so it cannot be derived
// from the application locale. Every value must map to exactly one URL per page, which is why
// app 3 (wallmek.se/en) and app 4 (wallmek.co.uk) need different locales in the first place.
// - en_SE is the English catalogue for the EU market: hreflang has no "EU" region, so it is
//   plain "en" and doubles as x-default for every visitor no other market claims.
// - wallmektools.com also serves Canada (see US_SERVED_COUNTRIES), so it gets en-CA as well.
// Locales missing here fall back to the naive "xx_YY" -> "xx-YY" swap.
export const HREFLANGS_BY_LOCALE = {
    sv_SE: ["sv-SE"],
    en_SE: ["en", "x-default"],
    en_US: ["en-US", "en-CA"],
    en_GB: ["en-GB"],
};

export const hreflangsForLocale = (locale = "") => HREFLANGS_BY_LOCALE[locale] || [locale.replace("_", "-")];

export const getLanguages = (languages = [], currentLocale) => {
    const productionEnabledLocales = process.env.REACT_APP_PRODUCTION_ENABLED_LOCALES || "";
    const currentSiteIsLive = productionEnabledLocales.indexOf(currentLocale) !== -1;

    if (!currentSiteIsLive) {
        return languages;
    }

    return languages.filter((item) => productionEnabledLocales.indexOf(item.locale) !== -1);
};

// Language URIs are protocol-relative ("//www.wallmek.se/sv"); compare only the host.
export const hostOfUri = (uri = "") =>
    uri
        .replace(/^https?:/i, "")
        .replace(/^\/\//, "")
        .split("/")[0]
        .toLowerCase();

// Markets that exist in the backend but must not be offered as a switch target yet.
// wallmek.co.uk ships behind basic auth (BASIC_AUTH_HOSTS in .production.env), so sending a
// visitor there lands them on a browser auth prompt. Matched on host rather than locale so a
// market can be hidden without touching the language list. Set in .production.env only, so QA
// and local can still move between every market.
export const isHiddenHost = (uri = "") =>
    (process.env.REACT_APP_HIDDEN_MARKET_HOSTS || "")
        .split(",")
        .some((hidden) => hidden.trim() && hidden.trim().toLowerCase() === hostOfUri(uri));

export const formatDomain = (uri) => {
    if (uri.lastIndexOf("/") === uri.length - 1) {
        return uri.slice(0, uri.length - 1);
    }

    return uri;
};

export const getGenericLink = (locale, languages) => {
    let uri;
    uri = languages?.find((i) => i.locale === locale)?.uri;

    if (!uri) {
        return "";
    }

    if (uri?.toLowerCase().indexOf("//") === 0) {
        uri = `https:${uri}`;
    }
    return uri;
};

export const getLinkWithSlug = (locale, hreflangs, languages) => {
    let uri;
    const formattedLocale = normaliseLocale(locale);
    const localeExistsAsHref = hreflangs.some((i) => i.locale === locale || i.locale === formattedLocale);

    if (!localeExistsAsHref || (languages && languages.locale === locale)) {
        uri = getGenericLink(locale, languages);
    } else {
        const language = hreflangs.find((i) => i.locale === locale || i.locale === formattedLocale);

        uri = language.uri;

        if (!uri) {
            return "";
        }

        if (uri.toLowerCase().indexOf("//") === 0) {
            uri = `https:${uri}`;
        }
    }

    return uri;
};

export const getHreflangs = (localeAlternates, languages = []) => {
    if (!localeAlternates) {
        return null;
    }

    // only a leading /sv|/en segment is a locale prefix — "/produkter/enhet" is not
    const stripLocaleSlug = (link) => (link || "").replace(/^\/(sv|en)(?=\/|$)/, "");

    return localeAlternates.flatMap((item) => {
        let matches;
        if (item.application_id) {
            matches = languages.filter((language) => language.id === item.application_id);
        } else {
            // CMS localizations are keyed by the application's external_id ("1_engb"), and one
            // CMS site can serve several applications (www.wallmek.se/en and wallmek.co.uk both
            // read "1_engb"), so every application with that external_id gets an alternate.
            // Older language payloads lack external_id; fall back to the locale suffix with
            // separators and case stripped ("1_engb" ends with "engb" for en_GB).
            matches = languages.filter((l) => !!l.external_id && l.external_id === item.locale);

            if (!matches.length) {
                const language = languages.find((l) => {
                    const target = normaliseLocale(l.locale);
                    return !!target && normaliseLocale(item.locale).endsWith(target);
                });
                matches = language ? [language] : [];
            }
        }

        return matches.map((language) => ({
            ...item,
            id: item.external_id,
            locale: language.locale,
            uri: `${formatDomain(language.uri)}${stripLocaleSlug(item.slug)}`,
        }));
    });
};
